import type { CreateDocumentRequest, DocumentResponse } from '@palaccount/contracts';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * Asking the worker for a PDF, and waiting for it — DECISION.md §2.13.
 *
 * NOT a react-query hook, and deliberately: this is not data a screen renders,
 * it is an action a button takes once. A `useQuery` would cache a document id
 * whose artifact is a one-off, and a `useMutation` would still leave the
 * polling to the caller. A function that resolves when the file is ready is
 * what a click handler actually wants.
 *
 * The poll is what makes the 202 tolerable. §2.13 puts rendering in a job
 * precisely so no request waits on Chromium, which means SOMEBODY has to wait
 * — and it should be the browser, where waiting is a spinner rather than a
 * held thread.
 */

/** How long a user will plausibly wait before the wait is the bug. */
const DEADLINE_MS = 45_000;
/** Fast at first (most documents finish inside a second), then backing off. */
const POLL_STEPS_MS = [300, 300, 500, 500, 800, 1200, 2000];

export class DocumentFailed extends Error {}
export class DocumentTimedOut extends Error {}

export async function requestDocument(request: CreateDocumentRequest): Promise<DocumentResponse> {
  const created = await api.documents.create({ body: request });
  if (created.status !== 202) throwApiError(created.status, created.body);

  const deadline = Date.now() + DEADLINE_MS;
  let step = 0;
  let latest = created.body;

  while (latest.state === 'pending') {
    if (Date.now() > deadline) {
      // The row is still there and the sweep will keep trying; what has run
      // out is the user's patience, so say that rather than pretending the
      // document failed.
      throw new DocumentTimedOut(latest.id);
    }
    await new Promise((resolve) =>
      setTimeout(resolve, POLL_STEPS_MS[Math.min(step, POLL_STEPS_MS.length - 1)]),
    );
    step += 1;

    const polled = await api.documents.get({ params: { documentId: latest.id } });
    if (polled.status !== 200) throwApiError(polled.status, polled.body);
    latest = polled.body;
  }

  if (latest.state === 'failed') throw new DocumentFailed(latest.lastError ?? '');
  return latest;
}

/**
 * The download URL for a finished document.
 *
 * A plain link rather than a fetch-and-blob: the session cookie rides along,
 * the browser's own download UI takes over, and a 300-page statement never
 * exists in the tab's memory.
 *
 * TODO(deploy): §2.15 makes this a 302 to a short-lived presigned R2 URL, so
 * the bytes stop passing through the application at all. The href does not
 * change; only what is behind it does.
 */
export function documentUrl(documentId: string): string {
  return `/api/documents/${documentId}/content`;
}

/** Ask for a document and hand the finished file to the browser. */
export async function downloadDocument(request: CreateDocumentRequest): Promise<void> {
  const document_ = await requestDocument(request);
  const anchor = document.createElement('a');
  anchor.href = documentUrl(document_.id);
  anchor.download = document_.filename;
  anchor.click();
}
